import { useState, useRef } from 'react';
import { products } from '@/mocks/products';

interface NewArrivalsProps {
  onProductClick: (link: string) => void;
}

// IDs of the most recently added products
const NEW_ARRIVAL_IDS = [33, 34, 35, 36, 37, 38, 39, 40];

const newArrivals = products.filter((p) => NEW_ARRIVAL_IDS.includes(Number(p.id)));

export default function NewArrivals({ onProductClick }: NewArrivalsProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const scroll = (dir: 'left' | 'right') => {
    if (!scrollRef.current) return;
    const amount = 300;
    scrollRef.current.scrollBy({ left: dir === 'right' ? amount : -amount, behavior: 'smooth' });
  };

  const handleScroll = () => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    const index = Math.round((scrollLeft / (scrollWidth - clientWidth)) * (newArrivals.length - 1));
    setActiveIndex(index);
  };

  return (
    <section className="px-4 py-10 md:py-14 bg-gradient-to-br from-rose-50 via-amber-50 to-teal-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="inline-flex items-center gap-1.5 bg-rose-500 text-white text-xs font-bold px-3 py-1 rounded-full animate-pulse">
                <i className="ri-sparkling-line"></i>
                Just Added
              </span>
              <span className="text-xs text-gray-500 font-medium">Updated this week</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800">New Arrivals 🆕</h2>
            <p className="text-sm text-gray-500 mt-0.5">Fresh picks for your feline — be the first to grab them</p>
          </div>
          {/* Desktop arrows */}
          <div className="hidden md:flex items-center gap-2">
            <button
              onClick={() => scroll('left')}
              className="w-10 h-10 flex items-center justify-center bg-white rounded-full shadow-md hover:shadow-lg border border-gray-100 text-gray-600 hover:text-teal-600 transition-all cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-xl"></i>
            </button>
            <button
              onClick={() => scroll('right')}
              className="w-10 h-10 flex items-center justify-center bg-white rounded-full shadow-md hover:shadow-lg border border-gray-100 text-gray-600 hover:text-teal-600 transition-all cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-xl"></i>
            </button>
          </div>
        </div>

        {/* Scrollable Cards */}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide -mx-4 px-4"
        >
          {newArrivals.map((product, idx) => (
            <div
              key={product.id}
              onClick={() => onProductClick(product.link)}
              className="flex-shrink-0 w-56 md:w-64 bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer snap-start group"
            >
              {/* Image */}
              <div className="relative overflow-hidden">
                <img
                  src={product.image}
                  alt={product.name}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = 'https://public.readdy.ai/ai/img_res/3bef5e4cde10ebd7944becfba93f5226.jpg';
                  }}
                  className="w-full h-44 md:h-52 object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                {/* NEW badge */}
                <div className="absolute top-3 left-3 flex items-center gap-1 bg-rose-500 text-white text-xs font-bold px-2.5 py-1 rounded-full shadow">
                  <i className="ri-sparkling-2-fill text-xs"></i>
                  NEW
                </div>
                {/* Arrival number */}
                <div className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center bg-white/90 backdrop-blur-sm rounded-full text-xs font-bold text-gray-700 shadow">
                  #{idx + 1}
                </div>
                {product.badge && (
                  <div className={`absolute bottom-3 left-3 px-2.5 py-0.5 rounded-full text-xs font-bold ${
                    product.badge === 'SALE' ? 'bg-rose-500 text-white' :
                    product.badge === 'BESTSELLER' ? 'bg-amber-500 text-white' :
                    product.badge === 'TOP RATED' ? 'bg-teal-500 text-white' :
                    'bg-gray-700 text-white'
                  }`}>
                    {product.badge}
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="p-4">
                <span className="text-xs font-medium text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full">
                  {product.category}
                </span>
                <h3 className="font-semibold text-gray-800 text-sm mt-2 mb-1 line-clamp-2 leading-snug min-h-[36px]">
                  {product.name}
                </h3>
                {/* Honest badge replacing fake stars */}
                <div className="flex items-center gap-1 mb-2">
                  <i className="ri-medal-line text-amber-500 text-xs"></i>
                  <span className="text-xs font-semibold text-amber-600">Highly Rated on Chewy</span>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-lg font-bold text-teal-600">${product.price}</span>
                    {product.originalPrice && (
                      <span className="text-xs text-gray-400 line-through">${product.originalPrice}</span>
                    )}
                  </div>
                  <button className="flex items-center gap-1 bg-teal-500 hover:bg-teal-600 text-white text-xs font-bold px-3 py-2 rounded-full transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-shopping-bag-3-line"></i>
                    Shop
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Dot indicators - mobile */}
        <div className="flex justify-center gap-1.5 mt-4 md:hidden">
          {newArrivals.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === activeIndex ? 'w-5 bg-teal-500' : 'w-1.5 bg-gray-300'
              }`}
            />
          ))}
        </div>

        {/* Bottom CTA strip */}
        <div className="mt-6 bg-white rounded-2xl px-5 py-4 flex flex-col sm:flex-row items-center justify-between gap-3 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-rose-100 rounded-full">
              <i className="ri-notification-3-line text-rose-500 text-lg"></i>
            </div>
            <div>
              <p className="font-semibold text-gray-800 text-sm">Never miss a new arrival</p>
              <p className="text-xs text-gray-500">We add fresh picks every week</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <i className="ri-time-line text-teal-500"></i>
            <span>Last updated: <strong className="text-gray-700">This week</strong></span>
            <span className="w-1.5 h-1.5 rounded-full bg-teal-400 animate-pulse inline-block"></span>
          </div>
        </div>
      </div>
    </section>
  );
}